import React, { useState, useEffect, useRef } from "react";
import { GoogleMap, Marker, InfoWindow, DirectionsRenderer, useJsApiLoader } from "@react-google-maps/api";
import { Image } from 'cloudinary-react';

const libraries = ["places"];

const SimpleMap = ({ marker, position, draggable }) => {
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_KEY,
    libraries,
  });

  const [center, setCenter] = useState({ lat: -36.848461, lng: 174.763336 });
  const [zoom, setZoom] = useState(12);
  const [cardOpen, setCardOpen] = useState(false);
  const [currentLocation, setCurrentLocation] = useState(position);
  const [directions, setDirections] = useState(null);
  const mapRef = useRef(null);

  useEffect(() => {
    if (position) {
      setCurrentLocation(position);
      setCenter({ lat: position.lat, lng: position.lng });
      setZoom(15);
    }
  }, [position]);

  useEffect(() => {
    if (marker && position && isLoaded) {
      const DirectionsService = new window.google.maps.DirectionsService();

      DirectionsService.route({
        origin: new window.google.maps.LatLng(position.lat, position.lng),
        destination: new window.google.maps.LatLng(marker.lat, marker.lng),
        travelMode: window.google.maps.TravelMode.WALKING,
      }, (result, status) => {
        if (status === window.google.maps.DirectionsStatus.OK) {
          setDirections(result);
        } else {
          console.error(`error fetching directions ${result}`);
        }
      });
    }
  }, [marker, position, isLoaded]);

  const resetBounds = () => {
    if (!window.google || !mapRef.current || !marker || !position) return;

    const bounds = new window.google.maps.LatLngBounds();

    const positionLatLng = new window.google.maps.LatLng(position.lat, position.lng);
    bounds.extend(positionLatLng);

    const markerLatLng = new window.google.maps.LatLng(marker.lat, marker.lng);
    bounds.extend(markerLatLng);

    if (bounds.getNorthEast().equals(bounds.getSouthWest())) {
      const extra = 0.001;
      const extendPoint1 = new window.google.maps.LatLng(bounds.getNorthEast().lat() + extra, bounds.getNorthEast().lng() + extra);
      const extendPoint2 = new window.google.maps.LatLng(bounds.getNorthEast().lat() - extra, bounds.getNorthEast().lng() - extra);
      bounds.extend(extendPoint1);
      bounds.extend(extendPoint2);
    }

    mapRef.current.fitBounds(bounds);
  };

  useEffect(() => {
    if (isLoaded && marker && position) {
      resetBounds();
    }
  }, [isLoaded, marker, position]);

  if (!isLoaded) return <div>Loading...</div>;

  return (
    <GoogleMap
      mapContainerStyle={{ height: "100%", width: "100%" }}
      center={center}
      zoom={zoom}
      options={{
        streetViewControl: false,
        disableDefaultUI: true,
        styles: [
          {
            featureType: "landscape.man_made",
            elementType: "geometry.fill",
            stylers: [{ color: "#eee5da" }],
          },
          {
            featureType: "landscape.natural",
            elementType: "geometry.fill",
            stylers: [{ color: "#eee5da" }],
          },
          {
            featureType: "landscape.natural.landcover",
            stylers: [{ color: "#bea380" }],
          },
          {
            featureType: "poi",
            elementType: "geometry",
            stylers: [{ visibility: "off" }],
          },
          {
            featureType: "poi",
            elementType: "labels",
            stylers: [{ visibility: "off" }],
          },
        ],
      }}
      onLoad={(map) => (mapRef.current = map)}
    >
      <DirectionsRenderer
        directions={directions}
        options={{
          polylineOptions: {
            strokeColor: '#000',
            strokeWeight: 5,
          },
        }}
      />

      {currentLocation && (
        <Marker
          key="currentLocation"
          position={currentLocation}
          icon="/images/current-location.png"
        />
      )}

      {marker && (
        <Marker
          key="shop"
          position={{ lat: Number(marker.lat), lng: Number(marker.lng) }}
          icon={{
            url: '/images/marker_black.png',
            scaledSize: new window.google.maps.Size(21, 32),
            anchor: new window.google.maps.Point(0, 32),
          }}
          onClick={() => setCardOpen(true)}
        >
          {cardOpen && (
            <InfoWindow onCloseClick={() => setCardOpen(false)}>
              <div className="c-map__card">
                {marker.banner && (
                  <Image
                    cloudName={process.env.REACT_APP_CLOUDINARY_CLOUD_NAME}
                    publicId={marker.banner}
                    className="c-map__card-image"
                  />
                )}

                <div className="c-map__card-content">
                  <div className="c-map__card-title">
                    <h4>{marker.name}</h4>
                  </div>

                  <p>{marker.description}</p>

                  <p>{marker.address}</p>
                </div>
              </div>
            </InfoWindow>
          )}
        </Marker>
      )}
    </GoogleMap>
  );
};

export default SimpleMap;